import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { MapPin, ArrowDown, CupSoda, Zap, Sandwich, Thermometer, RadioTower, PackageCheck } from "lucide-react";
import { scrollToId } from "@/App";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className={`block overflow-hidden pb-[0.12em] -mb-[0.12em] ${className}`}>
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const STOCK = [
    { label: "Chips & crisps", pct: 82 },
    { label: "Sodas", pct: 64 },
    { label: "Energy drinks", pct: 91 },
    { label: "Deli sandwiches", pct: 45 },
];

function TelemetryCard() {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 16 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 16 });

    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <div style={{ perspective: 1200 }} className="relative">
            <motion.div
                ref={ref}
                data-testid="hero-3d-telemetry-card"
                onMouseMove={onMove}
                onMouseLeave={onLeave}
                initial={{ opacity: 0, y: 60, rotateX: 12 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                className="relative rounded-3xl border border-[#A6CE39]/20 bg-[#122A16]/80 backdrop-blur-xl p-7 sm:p-8 shadow-[0_40px_80px_-20px_rgba(14,30,18,0.55)]"
            >
                <div style={{ transform: "translateZ(40px)" }}>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#A6CE39] opacity-60" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#A6CE39]" />
                            </span>
                            <span className="font-mono-brand text-[11px] tracking-[0.25em] text-[#A6CE39] uppercase">
                                HLV-01 · Live telemetry
                            </span>
                        </div>
                        <RadioTower className="h-4 w-4 text-[#A6CE39]/70" />
                    </div>

                    <div className="space-y-4">
                        {STOCK.map((s, i) => (
                            <div key={s.label} data-testid={`telemetry-stock-${i}`}>
                                <div className="flex justify-between text-xs mb-1.5">
                                    <span className="text-[#F9F8F3]/80 font-medium">{s.label}</span>
                                    <span className="font-mono-brand text-[#A6CE39]">{s.pct}%</span>
                                </div>
                                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                                    <motion.div
                                        className="h-full rounded-full bg-gradient-to-r from-[#3FAE4A] to-[#A6CE39]"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${s.pct}%` }}
                                        transition={{ duration: 1.4, delay: 1.4 + i * 0.15, ease: EASE }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-7 pt-5 border-t border-white/10 grid grid-cols-3 gap-3 text-center">
                        <div>
                            <Thermometer className="h-4 w-4 mx-auto mb-1 text-[#F58220]" />
                            <p className="font-mono-brand text-sm text-[#F9F8F3]">36°F</p>
                            <p className="text-[10px] text-[#F9F8F3]/50 uppercase tracking-wider">Cabinet</p>
                        </div>
                        <div>
                            <PackageCheck className="h-4 w-4 mx-auto mb-1 text-[#A6CE39]" />
                            <p className="font-mono-brand text-sm text-[#F9F8F3]">Thu 7am</p>
                            <p className="text-[10px] text-[#F9F8F3]/50 uppercase tracking-wider">Restock</p>
                        </div>
                        <div>
                            <Zap className="h-4 w-4 mx-auto mb-1 text-[#F58220]" />
                            <p className="font-mono-brand text-sm text-[#F9F8F3]">$0</p>
                            <p className="text-[10px] text-[#F9F8F3]/50 uppercase tracking-wider">Your cost</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

const FloatBadge = ({ icon: Icon, label, className, delay }) => (
    <motion.div
        data-testid={`hero-float-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay, ease: EASE }}
        className={`absolute z-10 ${className}`}
    >
        <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay }}
            className="flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-4 py-2 shadow-[0_16px_40px_-12px_rgba(14,30,18,0.35)] border border-[#1B4D1B]/10"
        >
            <Icon className="h-4 w-4 text-[#3FAE4A]" />
            <span className="text-xs font-semibold text-[#0E1E12] whitespace-nowrap">{label}</span>
        </motion.div>
    </motion.div>
);

export default function Hero() {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const yCard = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const yBlob = useTransform(scrollYProgress, [0, 1], [0, -140]);
    const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={sectionRef} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
            {/* botanical backdrop */}
            <motion.div style={{ y: yBlob }} className="absolute inset-0 pointer-events-none" aria-hidden>
                <div className="absolute -top-32 -right-32 h-[540px] w-[540px] rounded-full bg-[#A6CE39]/25 blur-[110px]" />
                <div className="absolute bottom-0 -left-40 h-[460px] w-[460px] rounded-full bg-[#3FAE4A]/18 blur-[110px]" />
                <span className="absolute -bottom-24 left-1/2 -translate-x-1/2 font-display font-black text-[26vw] leading-none text-outline select-none tracking-tighter">
                    HLV
                </span>
            </motion.div>

            <motion.div style={{ opacity }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
                <div className="grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
                            className="inline-flex items-center gap-2 rounded-full border border-[#3FAE4A]/30 bg-white/70 backdrop-blur px-4 py-1.5 mb-8"
                            data-testid="hero-service-badge"
                        >
                            <MapPin className="h-3.5 w-3.5 text-[#F58220]" />
                            <span className="font-mono-brand text-[11px] tracking-[0.2em] uppercase text-[#1B4D1B]">
                                Servicing Northern Virginia
                            </span>
                        </motion.div>

                        <h1
                            data-testid="hero-headline-masked"
                            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.04] tracking-tight text-[#0E1E12]"
                        >
                            <MaskedLine delay={0.45}>Healthy vending,</MaskedLine>
                            <MaskedLine delay={0.58}>
                                <span className="text-[#3FAE4A]">installed free</span> in
                            </MaskedLine>
                            <MaskedLine delay={0.71}>
                                your office<span className="text-[#F58220]">.</span>
                            </MaskedLine>
                        </h1>

                        <motion.p
                            data-testid="hero-subcopy"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                            className="mt-7 max-w-xl text-base sm:text-lg text-[#4A584C] leading-relaxed"
                        >
                            HealthLogic Vending places smart, self-monitoring machines stocked with
                            genuinely good snacks and drinks in Northern Virginia workplaces —
                            at zero cost to you. No contracts, no hassle, no junk.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                            className="mt-9 flex flex-wrap items-center gap-4"
                        >
                            <button
                                data-testid="hero-request-machine-cta"
                                onClick={() => scrollToId("#contact")}
                                className="group relative overflow-hidden rounded-full bg-[#0E1E12] text-white font-semibold px-8 py-4 text-sm transition-shadow duration-300 hover:shadow-[0_0_0_8px_rgba(63,174,74,0.15)]"
                            >
                                <span className="absolute inset-0 bg-[#3FAE4A] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                                <span className="relative">Request your free machine</span>
                            </button>
                            <button
                                data-testid="hero-how-it-works-cta"
                                onClick={() => scrollToId("#how-it-works")}
                                className="flex items-center gap-2 text-sm font-semibold text-[#0E1E12] px-2 py-4 group"
                            >
                                See how it works
                                <ArrowDown className="h-4 w-4 text-[#F58220] transition-transform duration-300 group-hover:translate-y-1" />
                            </button>
                        </motion.div>

                        <motion.div
                            data-testid="hero-stats-row"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 1.4 }}
                            className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
                        >
                            {[
                                ["$0", "cost to your office"],
                                ["48hr", "typical install window"],
                                ["24/7", "telemetry monitoring"],
                            ].map(([v, l]) => (
                                <div key={l}>
                                    <p className="font-display text-2xl font-extrabold text-[#0E1E12]">{v}</p>
                                    <p className="text-xs text-[#768578] uppercase tracking-[0.15em] mt-0.5">{l}</p>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    <motion.div style={{ y: yCard }} className="lg:col-span-5 relative">
                        <FloatBadge icon={CupSoda} label="Ice-cold sodas" className="-top-6 -left-4 sm:-left-10" delay={1.5} />
                        <FloatBadge icon={Sandwich} label="Deli sandwiches" className="top-1/3 -right-3 sm:-right-8" delay={1.7} />
                        <FloatBadge icon={Zap} label="Energy drinks" className="-bottom-5 left-8" delay={1.9} />
                        <TelemetryCard />
                    </motion.div>
                </div>
            </motion.div>
        </section>
    );
}
